<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import indowWindowPic from './images/indow-window-pic.jpg';
	import upperArrow from './images/upper-arrow.jpg';
</script>

<Section>
	<p>
		Welcome to our glossary of everything windows, interior storm window
		inserts, and energy efficiency! If you don’t see a definition here that you
		need, check out our storm window <a href="/resources/faq">FAQ</a>. If it’s
		not there either, please
		<a href="/about-indow/contact">let us know</a>.
	</p>
	<p>We’re here to help!</p>
	<Image
		src={indowWindowPic}
		alt="interior storm window inserts"
		class="px-4"
	/>
	<nav>
		<ul class="[&_a]:text-xl">
			<li>
				<a href="#acoustic">Acoustic</a>
			</li>

			<li>
				<a href="#energy">Energy Efficiency</a>
			</li>
			<li>
				<a href="#measurement">Measurement</a>
			</li>
			<li>
				<a href="#products">Products</a>
			</li>
		</ul>
	</nav>
</Section>
<hr />
<Section id="acoustic">
	<h2>Acoustic</h2>
	<h3>dba</h3>
	<p>
		A-weighted decibels. Measurement of sound as perceived by the human ear.
		Decibels relate to all sound possible (that can be perceived by say, a dog).
		A-weighted decibels are a scale of measurement for all sound that can be
		heard by humans, starting with zero as the lowest threshold of human hearing
		and going up to 110 dBA for jet fly-over/indoor rock band.
	</p>
	<h3>STC Rating</h3>
	<p>
		Sound Transmission Class rating. The higher the rating, the less noise
		penetration. A single pane window yields an STC rating of 26-28, meaning
		normal speech can be heard clearly. Dual-pane is rated 33-35, insulated
		walls come in at 34-39. Indow Accoustic Grade window inserts combined with
		double-pane windows are rated 42-45. Learn more about STC rating.
	</p>
	<h3>CFM (Cubic Feet Per Minute)</h3>
	<p>
		When a part of your home isn’t completely air sealed, there will be a
		leakage rate calculated using Cubic Feet Per Minute. The higher the CFM, the
		more rapid the air leak is.
	</p>
	<p>Single-pane window has a CFM of .5</p>
	<p>Double-pane window has a CFM of .1 – .3</p>
	<p>Single-pane window + Indow insert has a CFM of .024</p>
</Section>
<hr />
<Section id="energy">
	<h2>Energy Efficiency</h2>
	<h3>R-Value</h3>
	<p>
		R-value measures the thermal resistance (how much heat transfers through
		your walls, windows) of walls, windows, and other parts of a building. The
		greater your R-value of your windows, the greater the insulation they
		provide.
	</p>
	<p>
		An Indow window insert combined with a single pane window provides 94% of
		the thermal insulation of a standard double pane window.
	</p>
	<p>Single-pane + Indow window insert = R-1.87</p>
	<p>Double-pane = R-2.0</p>
	<p>Single-pane = R-1.0</p>
	<h3>Building Envelope</h3>
	<p>
		The walls, ceiling, and floor that together enclose the living space. The
		building envelope is basically the seal we put between ourselves and the
		outside elements: weather, noise, air. It’s the structure of our home that
		keeps us comfortable.
	</p>
</Section>
<hr />
<Section id="measurement">
	<h2>Measurement</h2>
	<h3>Out of Square</h3>
	<p>
		Because homes settle over time, most windows are not square. Even if your
		windows look square, they probably aren’t. In fact, 95% of windows are
		slight trapezoids or parallelograms. That’s why many traditional window
		inserts do not create a near airtight seal and why we use laser measurement
		to ensure precise fit in each window frame.
	</p>
	<h3>Frame Depth</h3>
	<p>
		The distance between your original window and the end of the flat surface
		extending from it. This is where your insert will be placed, so if the
		window sill protrudes past the wall, do not measure that part.
	</p>
	<h3>Frame Step</h3>
	<p>
		A flat surface extending out from your window which is parallel to your
		floor. Your window may have multiple frame steps leading up to your window.
		This is where your insert will be installed. When we ask you to measure a
		frame step or for you to install an insert on a frame step, we are asking
		for the largest continuously flat surface leading to your original window.
	</p>
</Section>
<hr />
<Section id="products">
	<h2>Products</h2>
	<h3>Mullion</h3>
	<p>
		A mullion divides a window (or window insert) into smaller, more manageable
		units. Mullions help support glass either vertically or horizontally. If you
		have older windows, this could be from when large sheets of glass were
		impossible to manufacture. Window inserts require mullions for support when
		placed in extremely large window frames (using two window inserts to cover
		the area). Multiple window inserts with a mullion support can make daily
		removal or cleaning easier.
	</p>
	<h3>Acrylic Glazing</h3>
	<p>
		Acrylic is a highly transparent material (more light transmissive than
		glass) and has a high strength to weight ratio.
	</p>
	<h3>Safety Chains</h3>
	<p>
		Small ball chains attached to the back of some window inserts. Can be
		connected to window frame via bracket hidden behind insert frame. Used to
		keep window insert in place in cases of severe venting (rare) or when one
		desires access to open and close window.
	</p>
	<h3>Venting</h3>
	<p>
		High wind pressure from the outside of leaky exterior windows and window
		insert can cause Indow insert to be pushed out of window frame in one or
		more corner. This is rare and can be solved by pushing window insert back
		into place.
	</p>
	<h3>Compression Tubing</h3>
	<p>
		Our window inserts are lined in silicone compression tubing. Our compression
		system is patented for the way it fits snugly in the window frame and fills
		any gaps to create a near airtight seal. The silicone is resilient and
		durable so that long exposure to sun does not cause fading and multiple
		removals do not cause damage.
	</p>
	<h3>Accessories (Window Insert)</h3>
	<p>
		Accessories for your window insert can help keep it in place if your insert
		is going in a skylight, is especially large, or your frame depth is
		especially deep or shallow. Here are some window accessories that can help:
	</p>
	<p>
		<strong>Grip Strips:</strong> provide a channel for your insert to keep it in
		place in the window frame. Use if your frame depth is very deep, too shallow,
		or made of slick material.
	</p>
	<p>
		<strong>Frame Swivel Clips:</strong> mount on the face of your window frame to
		hold the window insert in place. They are small plastic clips, held in place with
		a screw, that can be swiveled in front of the insert. Use if you have an especially
		large window insert, if you are concerned about venting, or are using an insert
		in a skylight.
	</p>
	<p>
		<strong>Retaining Pins:</strong> pins inserted into pre-drilled holes in your
		window frame to keep insert in place. Pins can be placed either in front of or
		behind insert. Pins extends into the window area. Use if you have an especially
		deep frame or if you are concerned about venting.
	</p>
	<p>
		<strong>L Brackets:</strong> a bracket that is L shaped attached to the inside
		of your window frame to keep your window insert in place. These provide a backing
		for your insert so it does not move too far into the frame. Use if you have an
		especially deep frame, are concerned about venting, or are using an insert in
		a skylight.
	</p>
	<p>
		Both Swivel Clips and Retaining Pins automatically come with your skylight
		insert order.
	</p>
	<h3>Legacy Insert</h3>
	<p>
		Our legacy inserts are those that came before the addition of our ABS
		carrier. They are still used for odd-shaped windows that need a non-rigid
		frame, which will be flexible to fit non-rectangular shapes.
	</p>
	<h3>ABS Carrier</h3>
	<p>
		Acrylonitrile Butadiene Styrene plastic carrier. The plastic frame between
		the acrylic glazing and silicone tube. The ABS carrier is rigid and holds
		both the acrylic glazing and silicone tube in place. It is present on all
		standard rectangular window inserts. It is replaced by a flexible silicone
		carrier on odd-shaped windows so that the window insert can bend to meet its
		shape.
	</p>
	<h3>Window Attachments</h3>
	<p>
		Window attachment can refer to anything added to your window to enhance or
		protect it: storm windows, window inserts, even a planter box. When we use
		it, we’re generally referring to window inserts, but may use the term
		gernally to refer to all storm windows.
	</p>
	<h3>Old-Growth Wood</h3>
	<p>
		Old-growth wood comes from old-growth trees that were allowed to grow for a
		long time. The rings of the tree are closer together (compared to younger
		trees used in modern construction) and are therefore more dense. This makes
		the wood more resistant to noise, bugs, and rot. Old-growth wood cannot be
		replaced because the resources are not available or should not be extracted.
	</p>
</Section>
<Image src={upperArrow} alt="corner of indow window insert" class="px-4" />
<GetAFreeEstimate />
